<template>
  <div style="padding: 1vw">
    <div class="container-wrapper">
      <div class="page-title-container">
        <div class="page-title">
          <h3 class="fw-bold" style="color: #000; font-size: 1.8rem">
            고객 대리 로그인
          </h3>
          <span class="pageDesc">
            고객 전화번호로 web-customer에 대리 로그인하여 해당 고객이 보는 쿠폰함/마이페이지 화면을 직접 확인할 수 있습니다.
            모든 발급 이력은 감사 로그에 기록됩니다.
          </span>
        </div>
      </div>

      <div class="container-wrapper" style="padding: 24px; background: #fff; border-radius: 12px; margin-top: 16px;">
        <a-alert
          type="warning"
          show-icon
          style="margin-bottom: 24px"
          message="주의사항"
          description="발급된 토큰은 1시간 동안 유효하며, 모든 사용 이력이 기록됩니다. 고객 데이터 열람 외의 결제/쿠폰 사용 등 민감 행위는 하지 마세요."
        />

        <a-form
          :model="form"
          :label-col="{ span: 4 }"
          :wrapper-col="{ span: 14 }"
          layout="horizontal"
          @finish="handleSubmit"
        >
          <a-form-item
            label="고객 전화번호"
            name="mobile"
            :rules="[{ required: true, message: '전화번호를 입력해주세요.' }]"
          >
            <a-input
              v-model:value="form.mobile"
              placeholder="010-0000-0000"
              allow-clear
              :maxlength="13"
            />
          </a-form-item>

          <a-form-item
            label="사유"
            name="reason"
            :rules="[
              { required: true, message: '대리 로그인 사유를 입력해주세요.' },
              { min: 5, message: '사유는 최소 5자 이상 입력해주세요.' },
            ]"
          >
            <a-textarea
              v-model:value="form.reason"
              placeholder="예) 고객 제보 확인 - 쿠폰 사용 여부"
              :rows="3"
              :maxlength="500"
              show-count
            />
          </a-form-item>

          <a-form-item :wrapper-col="{ offset: 4, span: 14 }">
            <a-button
              type="primary"
              html-type="submit"
              :loading="loading"
              class="yellow-button"
            >
              대리 로그인 링크 발급
            </a-button>
          </a-form-item>
        </a-form>

        <!-- 발급 결과 -->
        <div v-if="result" class="result-box">
          <h4>발급 완료</h4>
          <div class="result-row">
            <span class="label">대상 고객</span>
            <span class="value">{{ result.customer.name || '(이름 없음)' }} / {{ result.customer.mobile }}</span>
          </div>
          <div class="result-row">
            <span class="label">만료 시각</span>
            <span class="value">{{ formatDate(result.expiresAt) }}</span>
          </div>
          <div class="result-row">
            <span class="label">웹 고객 도메인</span>
            <span class="value">{{ webCustomerUrl }}</span>
          </div>
          <div class="button-row">
            <a-button
              type="primary"
              class="yellow-button"
              @click="openWebCustomer"
            >
              새 탭으로 열기
            </a-button>
            <a-button @click="copyLink">링크 복사</a-button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { message } from 'ant-design-vue';
import { impersonateCustomer } from './api.js';

export default {
  name: 'ImpersonateCustomer',
  data() {
    return {
      form: {
        mobile: '',
        reason: '',
      },
      loading: false,
      result: null,
      webCustomerUrl: process.env.VUE_APP_WEB_CUSTOMER_URL || '',
    };
  },
  computed: {
    impersonateLink() {
      if (!this.result?.token) return '';
      const base = this.webCustomerUrl.replace(/\/$/, '');
      return `${base}/impersonate?token=${encodeURIComponent(this.result.token)}`;
    },
  },
  methods: {
    async handleSubmit() {
      if (!this.form.mobile.trim()) {
        message.warning('전화번호를 입력해주세요.');
        return;
      }
      if (!this.form.reason.trim() || this.form.reason.trim().length < 5) {
        message.warning('사유를 5자 이상 입력해주세요.');
        return;
      }

      this.loading = true;
      this.result = null;
      try {
        const response = await impersonateCustomer({
          mobile: this.form.mobile.trim(),
          reason: this.form.reason.trim(),
        });
        this.result = response;
        message.success(
          `${response.customer.name || '대상 고객'} 대리 로그인 토큰 발급 완료`,
        );
      } catch (err) {
        console.error('대리 로그인 발급 실패', err);
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          '대리 로그인 발급에 실패했습니다.';
        message.error(Array.isArray(msg) ? msg.join(', ') : msg);
      } finally {
        this.loading = false;
      }
    },

    openWebCustomer() {
      if (!this.impersonateLink) {
        message.warning('발급된 링크가 없습니다.');
        return;
      }
      if (!this.webCustomerUrl) {
        message.error(
          'VUE_APP_WEB_CUSTOMER_URL 환경변수가 설정되어 있지 않습니다.',
        );
        return;
      }
      window.open(this.impersonateLink, '_blank', 'noopener,noreferrer');
    },

    async copyLink() {
      if (!this.impersonateLink) return;
      try {
        await navigator.clipboard.writeText(this.impersonateLink);
        message.success('대리 로그인 링크가 복사되었습니다.');
      } catch (err) {
        console.error('클립보드 복사 실패', err);
        message.error('클립보드 복사에 실패했습니다.');
      }
    },

    formatDate(iso) {
      if (!iso) return '';
      try {
        const d = new Date(iso);
        const pad = (n) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      } catch {
        return iso;
      }
    },
  },
};
</script>

<style scoped>
.container-wrapper {
  max-width: 960px;
}

.page-title-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
}

.pageDesc {
  color: #666;
  font-size: 14px;
  margin-top: 6px;
  display: block;
  line-height: 1.5;
}

.result-box {
  margin-top: 24px;
  padding: 20px;
  background: #fafafa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
}

.result-box h4 {
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.result-row {
  display: flex;
  padding: 6px 0;
  font-size: 14px;
}

.result-row .label {
  width: 120px;
  color: #888;
}

.result-row .value {
  flex: 1;
  color: #222;
  word-break: break-all;
}

.button-row {
  display: flex;
  gap: 8px;
  margin-top: 16px;
}
</style>
